/**
 * useSearchExecution — the search execution layer, extracted verbatim from
 * SgpFlowScreen.jsx `executeQuery` (god-file decomposition, same pattern as
 * useFilteredAndSortedOptions: move verbatim, pass a params object, identical
 * behavior).
 *
 * Responsibilities (unchanged):
 *   1. Reset ~20 pieces of results/filter state on a new (non-broaden) search
 *   2. Drive the asymptotic progress animation + status text
 *   3. Detect a sportsbook mentioned in the query (UI preselection hint)
 *   4. Temporal broadening rewrite when `broaden` is set
 *   5. Run the unified search pipeline (services/searchPipeline)
 *   6. Apply pipeline results back into component state (options, metadata,
 *      sort mode, reference time, requested line, SGP stats)
 *   7. Query history + metrics_queries bookkeeping
 *
 * The returned function is recreated every render (no useCallback), exactly
 * like the original inline closure, so closure-freshness semantics are
 * preserved.
 */

import { Platform } from 'react-native';
import Constants from 'expo-constants';
import { db, auth } from '../firebaseConfig';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { runSearchPipeline, hydrateResultsForRender } from '../services/searchPipeline';
import { normalizeQuery } from '../utils/sgpFlowUtils';
import { summarizeResults } from '../utils/loadingPhase';
import { countBackgroundQuery } from '../utils/resultCounts';
import { isFrozenDataActive } from '../utils/dataSource';
import { track } from '../utils/analytics';
import { payloadFixtureId as payloadFixtureIdFor } from '../utils/payloadFixtureId';

export function useSearchExecution(d) {
    const {
        // refs
        progressIntervalRef, abortRef, lastRawRef, mountedRef,
        // query context
        routePayload, betMode, bookOverride, userProfile, queryHistory, isLiveQuery,
        avgMs, filterSportsbooks,
        // state setters — results
        setResultsContextQuery, setErrorMsg, setNoResults, setIsInitializing,
        setLoading, setHasStartedSearch, setFixturesFoundNoOdds,
        setSgpGenerationStats, setResponseMetadata, setFetchedOptions,
        setMyBooksFetchedOptions, setSelectedLegs, setActiveFixtureId,
        setLastNotice, setCurrentPage, setClarification, setAutoBroadened,
        // state setters — filters/sort
        setFilterSportsbooks, setFilterMarkets, setFilterPlayers,
        setPlayerChipHeadshots, setFilterLegCount, setFilterDeepLinksOnly,
        setSearchText, setShowIntlSportsbooks, setShowDfsSportsbooks,
        setSortMethod, setSortBy, setBooksViewOverride,
        setDisableMinBooksFilter, setDisableMyBooksPriority, setBackendSorted,
        setFilterResultType, setFilterLineType, setFilterPositiveEvOnly, setRelaxMinBooksFromBg,
        // state setters — progress/status
        setProgress, setProgressText, setSearchStatus,
        // state setters — misc
        setPreferredSportsbook, setSearchedSportsbooks, setIsLiveQuery,
        setReferenceTime, setRequestedLine, setRequestedDirection,
        setDidBroaden, setCurrentQueryMetricId, setQueryFeedback, setAvgMs,
        // helpers from component scope
        _getPreferredOuterSort, registerForegroundQuery,
        completeForegroundQuery, trackQuerySuccess,
    } = d;

    // Core executor for nlOdds query
    const executeQuery = async (q, { broaden = false, noAutoBroaden = false } = {}) => {
        if (!q) return;
        setResultsContextQuery(String(q).trim());

        // Initialize search logs for debugging and metrics
        const searchLogs = [];
        const logEvent = (event, data = {}) => {
            const logEntry = { timestamp: Date.now(), event, ...data };
            searchLogs.push(logEntry);
            console.log('[SGP Log]', event, data);
        };
        
        // Normalize the query to expand team abbreviations
        const normalizedQuery = normalizeQuery(q);
        console.log('[SGP] Original query:', q);
        console.log('[SGP] Normalized query:', normalizedQuery);
        logEvent('search_started', { originalQuery: q, normalizedQuery, broaden });
        
        setErrorMsg('');
        setNoResults(false);
        setClarification(null); // ⭐ N2: clear any pending clarification chips
        setAutoBroadened(null); // ⭐ N3: clear any broadened-results banner
        setIsInitializing(false); // Done initializing, now showing loading state
        setLoading(true);
        setHasStartedSearch(true); // Mark that we've started a search
        
        // ⭐ Register this query for history tracking
        // Keep the id: completion must only close THIS search, not a newer one.
        const foregroundQueryId = registerForegroundQuery(q, { betMode, broaden });
        setFixturesFoundNoOdds(null); // Reset marker for new search
        setSgpGenerationStats(null); // Clear previous stats
        setResponseMetadata(null); // Clear response metadata for new search
        setProgress(0); setProgressText('Understanding request…');
        if (!broaden) {
            setFetchedOptions([]);
            setMyBooksFetchedOptions([]);
            setSelectedLegs([]);
            setActiveFixtureId(null);
            setLastNotice('');
            setCurrentPage(1); // Reset pagination on new search
            // Reset filters on new search
            setFilterSportsbooks([]);
            setFilterMarkets([]);
            setFilterPlayers([]);
            setPlayerChipHeadshots({});
            // Clear any stale sportsbook preference from sessionStorage so it doesn't
            // bleed into this new query via the background-results handler below.
            try { window.sessionStorage.removeItem('sgp:preferredSportsbook'); } catch {}
            setFilterLegCount(null);
            setFilterDeepLinksOnly(false);
            setSearchText('');
            setShowIntlSportsbooks(false);
            setShowDfsSportsbooks(false);
            setSortMethod('ev');
            setSortBy(_getPreferredOuterSort('ev')); // Respect My Books mode; overridden to 'relevance' for specific-line backend-sorted queries
            // Reset the All Books / My Books toggle so it doesn't carry over from the
            // previous query. The user's saved preference (defaultBookSort) re-applies.
            setBooksViewOverride(null);
            // Reset per-query overrides — user preference (minBooks, defaultBookSort) should
            // re-apply on every new query. The user must explicitly opt out each time.
            setDisableMinBooksFilter(false);
            setDisableMyBooksPriority(false);
            // These used to survive into the next search: a "Team Odds" or "Main"
            // choice, +EV only, or a background result's relaxed floor silently
            // hid the new query's results. +EV only returns to the saved preference.
            setFilterResultType?.('all');
            setFilterLineType?.('all');
            setFilterPositiveEvOnly?.(!!userProfile?.preferences?.filterPositiveEvOnly);
            setRelaxMinBooksFromBg?.(false);
            setBackendSorted(false); // Will be set to true when results arrive
        }
        lastRawRef.current = '';
        mountedRef.current = true;

        try {
            // Clear any existing progress interval to prevent jumping
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
                progressIntervalRef.current = null;
            }
            
            // Progress animation with asymptotic curve (never stalls, slows down naturally)
            const startTs = Date.now();
            // Use actual average time for accurate progress (no cap)
            const target = Math.max(5000, avgMs); // Minimum 5 seconds, no maximum cap
            const interval = setInterval(() => {
                const elapsed = Date.now() - startTs;
                
                // Asymptotic progress: approaches 100% but never reaches it
                // Formula: 100 * (1 - e^(-3 * elapsed/target))
                // This creates smooth deceleration without hard stops
                const ratio = elapsed / target;
                const pct = Math.floor(100 * (1 - Math.exp(-3 * ratio)));
                
                setProgress(Math.min(99, pct));
                setSearchStatus(prev => ({ ...prev, elapsedMs: elapsed }));
            }, 100);
            progressIntervalRef.current = interval;
            // Determine functions URL: check for USE_EMULATOR flag, otherwise use production
            const isLocalhost = Platform.OS === 'web' && 
                (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
            
            // ⭐ Check if emulator should be used (only if USE_EMULATOR=true in env or query param)
            const useEmulator = isLocalhost && (
                (typeof process !== 'undefined' && process?.env?.USE_EMULATOR === 'true') ||
                (typeof window !== 'undefined' && window.location?.search?.includes('emulator=true'))
            );
            
            const base = useEmulator
                ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1' // Emulator
                : (
                    (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
                    || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
                    || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
                    || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
                    || 'https://us-central1-bet-optimized-fe922.cloudfunctions.net' // Production
                );
            console.log('[SGP] Functions base URL:', base, '(localhost:', isLocalhost, ', useEmulator:', useEmulator, ')');
            setProgressText('Understanding your query...');
            setSearchStatus({ step: 'Analyzing request', fixturesFound: 0, resultsFound: null, elapsedMs: 0 });
            const controller = new AbortController();
            abortRef.current = controller;
            
            // Smart sportsbook detection: check if query mentions a specific sportsbook
            const sportsbookKeywords = {
                // Big 5 US books
                'fanduel': 'FanDuel', 'fd': 'FanDuel',
                'draftkings': 'DraftKings', 'dk': 'DraftKings',
                'caesars': 'Caesars', 'czr': 'Caesars',
                'betmgm': 'BetMGM', 'mgm': 'BetMGM', 'bet mgm': 'BetMGM',
                'fanatics': 'Fanatics',
                // US regional / mid-tier
                'betrivers': 'BetRivers', 'bet rivers': 'BetRivers', 'rivers': 'BetRivers',
                'pointsbet': 'PointsBet',
                'espn bet': 'ESPNBet', 'espnbet': 'ESPNBet',
                'hard rock': 'Hard Rock Bet', 'hardrock': 'Hard Rock Bet',
                'wynnbet': 'WynnBET', 'wynn': 'WynnBET',
                'betparx': 'BetPARX', 'parx': 'BetPARX',
                'bally': 'Bally Bet', 'bally bet': 'Bally Bet',
                'superbook': 'SuperBook',
                'circa': 'Circa Sports', 'circa sports': 'Circa Sports',
                'unibet': 'Unibet',
                'betfred': 'Betfred',
                'bet365': 'bet365', 'b365': 'bet365',
                // EV / sharp books
                'novig': 'Novig',
                'prophet x': 'Prophet X', 'prophetx': 'Prophet X',
                'pinnacle': 'Pinnacle',
                'fliff': 'Fliff',
                'sporttrade': 'Sporttrade',
                // Canada
                'thescore': 'theScore', 'the score': 'theScore',
                // SGP-tier international books
                'blue book': 'Blue Book', 'bluebook': 'Blue Book',
                'william hill': 'William Hill', 'williamhill': 'William Hill',
                'betfair': 'Betfair',
                'betway': 'Betway',
                'betsson': 'Betsson',
                'bwin': 'bwin',
                'leovegas': 'LeoVegas', 'leo vegas': 'LeoVegas',
                'midnite': 'Midnite',
                'sportzino': 'Sportzino',
                // Offshore
                'bovada': 'Bovada',
                'betonline': 'BetOnline', 'bet online': 'BetOnline',
                'mybookie': 'MyBookie', 'my bookie': 'MyBookie',
                'bookmaker': 'Bookmaker',
            };
            
            const queryLower = q.toLowerCase();
            let detectedSportsbook = null;
            for (const [keyword, bookName] of Object.entries(sportsbookKeywords)) {
                if (queryLower.includes(keyword)) {
                    detectedSportsbook = bookName;
                    break;
                }
            }
            
            // ⭐ For ANY query mentioning a sportsbook, set it as preferred for card preselection
            // and auto-select in the filter bar
            if (detectedSportsbook) {
                console.log(`[SGP] 🎯 Sportsbook detected in query: ${detectedSportsbook} - setting as preferred`);
                setPreferredSportsbook(detectedSportsbook);
                setFilterSportsbooks([detectedSportsbook]);
            } else {
                setPreferredSportsbook(null);
            }
            
            let sportsbooksParam = null;
            let searchedBooks = [];
            
            // Check for book override first
            if (bookOverride) {
                // User has selected a specific book override
                sportsbooksParam = bookOverride;
                searchedBooks = [bookOverride];
            } else if (detectedSportsbook) {
                // User specified a sportsbook in query - use only that one
                sportsbooksParam = detectedSportsbook;
                searchedBooks = [detectedSportsbook];
            } else {
                // No sportsbook specified - use top 20 popular sportsbooks
                const top20Books = [
                    'DraftKings',
                    'FanDuel', 
                    'Caesars',
                    'BetMGM',
                    'PointsBet',
                    'BetRivers',
                    'WynnBet',
                    'Barstool',
                    'FOX Bet',
                    'Unibet',
                    'Bet365',
                    'William Hill',
                    'SugarHouse',
                    'Betfred',
                    'SuperBook',
                    'Circa Sports',
                    'Westgate',
                    'SI Sportsbook',
                    'BetUS',
                    'MyBookie'
                ];
                
                // If user has custom preferences, merge them with top 20
                const userSportsbooks = userProfile?.preferences?.selectedSportsbooks || [];
            if (userSportsbooks.length > 0) {
                    const combined = [...new Set([...userSportsbooks, ...top20Books])];
                sportsbooksParam = combined.join(',');
                    searchedBooks = combined;
                } else {
                    sportsbooksParam = top20Books.join(',');
                    searchedBooks = top20Books;
            }
            }
            setSearchedSportsbooks(searchedBooks);
            
            // When broadening, simplify the query to just get all games for the sport/league
            let finalPrompt = normalizedQuery;
            if (broaden) {
                // Generic broaden strategy: extract sport/league/date, remove specific restrictions
                // This works for ANY sport, not just hardcoded ones
                
                // List of common sport/league keywords to preserve
                const sportKeywords = ['nba', 'nfl', 'nhl', 'mlb', 'mls', 'ufc', 'pga', 
                                      'basketball', 'football', 'hockey', 'baseball', 'soccer', 'tennis',
                                      'ncaa', 'ncaaf', 'ncaab', 'college', 'wnba', 'cfl', 'premier league',
                                      'champions league', 'europa league', 'la liga', 'serie a', 'bundesliga',
                                      'ligue 1', 'championship'];
                
                // List of common date keywords to preserve
                const dateKeywords = ['today', 'tonight', 'tomorrow', 'this weekend', 'weekend',
                                     'next week', 'this week', 'monday', 'tuesday', 'wednesday',
                                     'thursday', 'friday', 'saturday', 'sunday'];
                
                // Extract sport/league terms from query
                const words = queryLower.split(/\s+/);
                const foundSports = [];
                const foundDates = [];
                
                for (let i = 0; i < words.length; i++) {
                    const word = words[i];
                    const twoWords = words.slice(i, i + 2).join(' ');
                    const threeWords = words.slice(i, i + 3).join(' ');
                    
                    // Check multi-word league names first
                    if (sportKeywords.includes(threeWords)) {
                        foundSports.push(threeWords);
                        i += 2;
                    } else if (sportKeywords.includes(twoWords)) {
                        foundSports.push(twoWords);
                        i += 1;
                    } else if (sportKeywords.includes(word)) {
                        foundSports.push(word);
                    }
                    
                    if (dateKeywords.includes(twoWords)) {
                        foundDates.push(twoWords);
                        i += 1;
                    } else if (dateKeywords.includes(word)) {
                        foundDates.push(word);
                    }
                }
                
                // Build temporally broadened query - KEEP original specificity
                // Only expand time range, don't add "all markets"
                const datePart = foundDates.length > 0 ? foundDates.join(' ') + ' and tomorrow and this week' : 'today, tomorrow, and this week';
                
                // Replace or append temporal expansion while keeping original query specificity
                let expandedQuery = normalizedQuery;
                if (foundDates.length > 0) {
                    // Replace existing date with expanded date range
                    for (const dateWord of foundDates) {
                        expandedQuery = expandedQuery.replace(dateWord, datePart);
                        break; // Only replace first occurrence
                    }
                } else {
                    // Append temporal range if no date specified
                    expandedQuery = normalizedQuery + ' ' + datePart;
                }
                
                finalPrompt = expandedQuery;
                
                console.log('[SGP] Temporally broadened query from:', normalizedQuery);
                console.log('[SGP] Broadened to:', finalPrompt);
                console.log('[SGP] Strategy: Keep original markets/teams, expand time only');
            }
            
            const t0 = Date.now();
            
            // ⚡ Unified search pipeline — shared with BackgroundQueryContext.processQuery.
            // See website/services/searchPipeline.js. Handles SGP intent detection
            // (with cache-skip rules for book-scoped and league-only queries),
            // queryCachedSGPs shortcut, queryWithCacheFallback fallback, and the
            // straight / SGP item transforms (ghost filter, EV sort, 4h stale drop).
            // Previously this flow was duplicated and had drifted from background,
            // causing foreground searches to silently fail in cases background handled.
            const payloadFixtureId = payloadFixtureIdFor(routePayload, q);

            const payloadIsLive = !!(routePayload?.isLive);
            const promptIsLive = /\blive\b/i.test(finalPrompt);
            const _isLiveQuery = payloadIsLive || promptIsLive || betMode === 'live';
            if (_isLiveQuery !== isLiveQuery) setIsLiveQuery(_isLiveQuery);

            const isPayloadSGP = routePayload?.fromCachedSGP === true || routePayload?.mode === 'sgp';
            const pipelinePrefer = (betMode === 'sgp' || betMode === 'sgpx' || isPayloadSGP) ? 'sgp' : 'straight';

            const pipelineResult = await runSearchPipeline(
                finalPrompt,
                {
                    fixtureId: payloadFixtureId,
                    league: routePayload?.league || null,
                    sport: routePayload?.sport || null,
                    isLive: _isLiveQuery,
                    prefer: pipelinePrefer,
                    broaden,
                    // ⭐ N3: "Undo" from the broadened-results banner — backend skips
                    // related-market/LLM auto-broaden and returns exact matches only.
                    noAutoBroaden,
                    defaults: {
                        myBooks: userProfile?.preferences?.myBooks || [],
                        selectedSportsbooks: [...new Set([
                            ...(userProfile?.preferences?.selectedSportsbooks || []),
                            ...(filterSportsbooks || []),
                        ])],
                        preferredLeagues: userProfile?.preferences?.tickerLeagues || [],
                        recentPrompts: (queryHistory || []).map((entry) => entry?.prompt).filter(Boolean).slice(0, 20),
                        broaden,
                    },
                },
                {
                    userProfile,
                    signal: controller.signal,
                    onStatus: ({ stage, progressMessage }) => {
                        if (stage === 'sgp_cache_check') {
                            setProgressText('⚡ Checking pre-generated SGP cache...');
                            setSearchStatus(prev => ({ ...prev, step: 'Checking SGP cache' }));
                            logEvent('sgp_cache_check');
                        } else if (stage === 'querying' || stage === 'cache_miss') {
                            setProgressText('🔍 Analyzing your request...');
                            setSearchStatus(prev => ({ ...prev, step: 'Processing query' }));
                        } else if (stage === 'cache_hit') {
                            setProgressText('⚡ Found cached data');
                            setSearchStatus(prev => ({ ...prev, step: 'Loading cached data' }));
                        } else if (stage === 'processing') {
                            setProgressText(progressMessage || 'Processing results...');
                            setSearchStatus(prev => ({ ...prev, step: 'Processing results' }));
                        }
                    },
                }
            );

            logEvent('query_complete', { cached: pipelineResult.cached, success: pipelineResult.success });
            if (pipelineResult.cancelled) return; // Cancel button — the screen already navigated away

            if (!pipelineResult.success) {
                throw new Error(pipelineResult.error || 'Query failed');
            }

            // ⭐ N2: Clarify-or-abstain — the backend declined to guess between
            // multiple candidate teams (e.g. "rangers" → NHL vs MLB). Store the
            // clarification payload; the screen renders tappable chips whose
            // revisedPrompt re-fires executeQuery deterministically.
            if (pipelineResult.needsClarification && pipelineResult.clarification) {
                logEvent('needs_clarification', {
                    term: pipelineResult.clarification.term,
                    options: (pipelineResult.clarification.options || []).map(o => o.team),
                });
                setClarification(pipelineResult.clarification);
                setNoResults(false);
                setDidBroaden(false);
                try {
                    await completeForegroundQuery([], { cached: false, sgpCount: 0, queryId: foregroundQueryId });
                } catch (historyErr) {
                    console.warn('[SGP] Error saving to query history:', historyErr);
                }
                return; // finally block still clears progress + loading
            }

            const {
                transformedResults,
                myBooksTransformed,
                sgpCount,
                isSGPResponse,
                _preferredSportsbook: pipelinePreferred,
                _lineMetadata,
                _queryClassification,
                cached: pipelineCached,
                rawBody,
                durationMs,
                referenceTime: _pipelineRefTime,
            } = pipelineResult;

            // Keep referenceTime in sync — null clears it (live mode), ISO string sets it (freeze mode)
            setReferenceTime(_pipelineRefTime || null);

            // Preferred sportsbook from backend — overrides the UI-side keyword
            // detection that ran earlier in executeQuery because backend
            // classification is the authoritative source.
            if (rawBody?._bookUnavailable) {
                // The requested book priced none of these results (e.g. Caesars
                // SGPs in the May 2026 snapshot). Filtering to it would hide
                // everything; show all books and let the banner explain.
                setPreferredSportsbook(null);
                setFilterSportsbooks([]);
            } else if (pipelinePreferred) {
                setPreferredSportsbook(pipelinePreferred);
                setFilterSportsbooks([pipelinePreferred]);
            }

            // Response metadata drives the "resolution" display animation.
            setResponseMetadata({
                _queryClassification,
                _detectedPlayer: rawBody?._detectedPlayer || rawBody?.player,
                _detectedTeam: rawBody?._detectedTeam,
                _homeTeam: rawBody?._homeTeam,
                _awayTeam: rawBody?._awayTeam,
                _league: rawBody?._league || rawBody?.league,
                _market: rawBody?._market || rawBody?.market || _lineMetadata?.market,
                _preferredSportsbook: pipelinePreferred,
                _lineThreshold: _lineMetadata?.lineThreshold,
                _lineDirection: _lineMetadata?.lineDirection,
                _resolvedQuery: rawBody?._resolvedQuery,
                _queryResolution: rawBody?._queryResolution,
                _resultValidation: rawBody?._resultValidation,
                _matchup: rawBody?._matchup,
                detectedMarketInfo: rawBody?.detectedMarketInfo,
                _debug_path: rawBody?._debug_path || null,
                _resultType: rawBody?._resultType || null,
                _message: rawBody?._message || pipelineResult.progressMessage || null,
                _bookUnavailable: rawBody?._bookUnavailable || null,
                _failureKind: rawBody?._failureKind || null,
                freeze_mode: !!rawBody?.freeze_mode,
                _metaSource: rawBody?.meta?.source || rawBody?.source || null,
                _cached: rawBody?.meta?.cached ?? rawBody?.cached ?? null,
                _cacheAgeSeconds: rawBody?.meta?.cache_age_seconds ?? null,
                _durationMs: durationMs,
                results: transformedResults,
            });

            // Requested line centers the alt-line cards (e.g. "pistons +14 turnovers").
            if (_lineMetadata?.lineThreshold !== null && _lineMetadata?.lineThreshold !== undefined) {
                setRequestedLine(_lineMetadata.lineThreshold);
                setRequestedDirection(_lineMetadata.lineDirection || null);
            } else {
                setRequestedLine(null);
                setRequestedDirection(null);
            }

            // Respect backend pre-sort for line-based queries; otherwise
            // re-sort by canonical EV (min-of-all-methods) to match the
            // frontend badge display.
            if (rawBody?._backendSorted) {
                const qType = rawBody?._queryClassification?.type;
                // FUTURES: backend order is future match, then favorites, Yes/No pairs together.
                const isLineBased = qType === 'PLAYER_PROP' || qType === 'SPECIFIC_PLAYER_MARKET' || qType === 'FUTURES';
                setBackendSorted(true);
                setSortBy(isLineBased ? 'relevance' : _getPreferredOuterSort('ev'));
            } else {
                setBackendSorted(true);
                setSortBy(_getPreferredOuterSort('ev'));
            }

            // Hydrate into UnifiedBetCard-ready options. Same helper is used by
            // the background-results handoff path so both entry points produce
            // identical card shapes.
            const hydrated = hydrateResultsForRender(transformedResults);
            setFetchedOptions(hydrated);
            // Real counts for the loader's completion moment. nlOdds answers in
            // one response, so these are known only now (Fixtures used to stay 0).
            const _summary = summarizeResults(hydrated);
            // What the backend scanned for this answer (frozen snapshot paths report it).
            const _scan = rawBody?._debug_path || {};
            setSearchStatus(prev => ({
                ...prev,
                fixturesFound: _summary.games,
                resultsFound: _summary.bets,
                marketsScanned: Number.isFinite(_scan.marketsRead) ? _scan.marketsRead : null,
                candidates: Number.isFinite(_scan.poolSize) ? _scan.poolSize : null,
            }));
            // My Books parallel set — hydrate and store separately so toggling
            // between All Books and My Books is instant with no re-fetch.
            const _myBooksHydrated = myBooksTransformed && myBooksTransformed.length > 0
                ? hydrateResultsForRender(myBooksTransformed) : [];
            setMyBooksFetchedOptions(_myBooksHydrated);
            // Same rule as View from Background (utils/resultCounts): when the search
            // found lines but none meet the min-books floor, open relaxed instead of
            // "Filters hid every result" (Royals NRFI, Oct 1, 2026).
            try {
                const _relax = countBackgroundQuery({
                    results: hydrated,
                    myBooksResults: _myBooksHydrated,
                    prompt: finalPrompt,
                    _queryClassification,
                    _preferredSportsbook: pipelinePreferred || null,
                    referenceTime: _pipelineRefTime || null,
                }, userProfile).relaxMinBooks;
                if (_relax) {
                    setDisableMinBooksFilter(true);
                    setRelaxMinBooksFromBg?.(true);
                }
            } catch (relaxErr) {
                console.warn('[search] relax check failed:', relaxErr?.message);
            }

            if (sgpCount > 0) {
                setSgpGenerationStats({
                    combinationsTested: 0,
                    marketsUsed: 0,
                    totalSgps: sgpCount,
                });
            } else {
                setSgpGenerationStats(null);
            }

            setFixturesFoundNoOdds(null);
            setDidBroaden(broaden);
            // ⭐ N3: surface the backend's broaden/fallback label — only when there
            // are results to caveat (a failed broaden shows the empty state instead).
            setAutoBroadened(hydrated.length > 0 ? (pipelineResult.broadened || null) : null);
            logEvent('search_completed', {
                finalResultsCount: hydrated.length,
                sgpCount,
                cached: pipelineCached,
                isSGPResponse,
                broaden,
            });

            if (hydrated.length === 0) {
                console.log('[SGP] No results from unified pipeline, setting noResults=true');
                setNoResults(true);
            }

            try {
                await completeForegroundQuery(hydrated, { cached: pipelineCached, sgpCount, queryId: foregroundQueryId });
            } catch (historyErr) {
                console.warn('[SGP] Error saving to query history:', historyErr);
            }

            if (!broaden) {
                // nlOdds counts searches itself now and returns the usage as `quota`.
                await trackQuerySuccess(hydrated.length > 0, rawBody?.quota);
            }
            track(hydrated.length > 0 ? 'search' : 'search_zero', { source: routePayload?.fromTicker ? 'ticker' : 'search', results: hydrated.length, broaden: !!broaden, cached: !!pipelineCached });

            try {
                const durationMs = Date.now() - t0;
                const u = auth?.currentUser;
                const uid = u?.uid || 'anon';
                const ref = collection(db, 'metrics_queries');
                const docRef = await addDoc(ref, {
                    uid,
                    prompt: q,
                    broaden,
                    durationMs,
                    resultsCount: hydrated.length,
                    resultsWithOdds: hydrated.length,
                    resultsWithoutOdds: 0,
                    source: routePayload?.fromTicker ? 'ticker' : 'search',
                    // Admin "Frozen" searches read the May snapshot; keep them out of live stats.
                    data_source: isFrozenDataActive() ? 'frozen' : 'live',
                    cached: pipelineCached,
                    feedback: null,
                    logs: searchLogs,
                    createdAt: serverTimestamp(),
                });
                setCurrentQueryMetricId(docRef.id);
                setQueryFeedback(null);
                setAvgMs(prev => Math.round((prev * 0.7) + (durationMs * 0.3)));
            } catch (err) {
                console.error('[SGP] Error saving metrics:', err);
            }
        } catch (e) {
            if (e?.name === 'AbortError' && !e?.message?.includes('timed out')) return; // User-initiated cancel
            const msg = e?.message || String(e);
            // ⭐ FIX: Replace cryptic abort message with friendly timeout message
            const isAbort = e?.name === 'AbortError' || msg.includes('aborted') || msg.includes('abort');
            setErrorMsg(isAbort 
              ? 'Query timed out — the search took too long. Try a more specific query.'
              : msg);
        } finally {
            // Clear progress interval so progress can show 100%
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
                progressIntervalRef.current = null;
            }
            setProgress(100);
            setProgressText('Done');
            setSearchStatus(prev => ({ ...prev, step: 'Complete' }));
            // Keep loading screen visible long enough for user to see 100% / "Done" then transition to results
            const completionDelay = 500;
            setTimeout(() => {
                if (mountedRef.current) setLoading(false);
            }, completionDelay);
        }
    };

    return executeQuery;
}
